import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { AuthService, ConfigService } from '@assist/shared/data';
import {
  BehaviorSubject,
  Observable,
  of,
  throwError,
  catchError,
  defaultIfEmpty,
  filter,
  map,
  switchMap,
  take,
  timer,
} from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({ providedIn: 'root' })
export class AuthInterceptor implements HttpInterceptor {
  // Jelikoz je refresh token One time use, tak muze dochazet k tomu, ze v jeden moment vice tabu chce refreshovat stejny token
  // namisto veli sloziteho lockocani napriic taby, coz je hodne zavisle na podpore browseru (a panely v sobe maji neco z prvovalecne republiky)
  // bude lepsis to delat pesimistick.. kdyz nepojde refresh, tak se nejdri ve az 2 sec checkuje, ejslti se nezmenil token
  // a zkusi se to znovu - po 100ms.
  private readonly apiRefreshFallbackCheckIntervalMs = 100;
  private readonly apiRefreshFallbackMaxWaitMs = 2000;
  private authService = inject(AuthService);
  private configService = inject(ConfigService);
  private translateService = inject(TranslocoService);
  private oauthTokenObtaining = false;
  private obtainTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private apiTokenObtaining = false;
  private apiTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private apiTokenRefreshing = false;
  private oauthTokenRefreshing = false;

  private apiRefreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  msUrls = ['https://graph.microsoft.com'];
  googleUrls = [
    'https://www.googleapis.com',
    'https://people.googleapis.com',
    'https://lh3.googleusercontent.com',
    'https://admin.googleapis.com',
  ];
  apiUrls: string[] = [];

  constructor() {
    this.apiUrls.push(this.configService.value.apiURL);
    if (this.configService.value.calendarURL && this.configService.value.calendarURL != '') {
      this.apiUrls.push(this.configService.value.calendarURL);
    }
  }

  private addTokenHeader(req: HttpRequest<any>, token: string) {
    return req.clone({
      headers: req.headers.set('Authorization', 'Bearer ' + token),
    });
  }

  isToAPI(url: string) {
    return this.apiUrls.some((apiUrl) => url.toUpperCase().startsWith(apiUrl.toUpperCase()));
  }

  isToMS(url: string) {
    return this.msUrls.some((apiUrl) => url.toUpperCase().startsWith(apiUrl.toUpperCase()));
  }

  isToGoogle(url: string) {
    return this.googleUrls.some((apiUrl) => url.toUpperCase().startsWith(apiUrl.toUpperCase()));
  }

  private waitForApiRefreshFromAnotherTab(refreshTokenUsedForRequest: string): Observable<string | null> {
    const checksCount = Math.floor(this.apiRefreshFallbackMaxWaitMs / this.apiRefreshFallbackCheckIntervalMs) + 1;

    return timer(0, this.apiRefreshFallbackCheckIntervalMs).pipe(
      take(checksCount),
      map(() => {
        const currentAccessToken = this.authService.getApiToken();
        const currentRefreshToken = this.authService.getApiRefreshToken();

        return currentAccessToken && currentRefreshToken && currentRefreshToken !== refreshTokenUsedForRequest
          ? currentAccessToken
          : null;
      }),
      filter((token): token is string => token !== null),
      take(1),
      defaultIfEmpty(null)
    );
  }

  private retryApiRequestAfterAnotherTabRefresh(
    request: HttpRequest<any>,
    next: HttpHandler,
    refreshTokenUsedForRequest: string,
    error: unknown
  ): Observable<any> {
    this.apiTokenRefreshing = false;

    return this.waitForApiRefreshFromAnotherTab(refreshTokenUsedForRequest).pipe(
      switchMap((token) => {
        if (token) {
          this.apiTokenSubject.next(token);
          const authReq = this.addTokenHeader(request, token);
          return next.handle(authReq).pipe(
            catchError((err: HttpErrorResponse) => {
              if (err.status == 401) {
                this.authService.logout();
              }
              return throwError(() => err);
            })
          );
        }

        this.authService.logout();
        return throwError(() => error);
      })
    );
  }

  handle401API(request: HttpRequest<any>, next: HttpHandler): any {
    const accessToken = this.authService.getApiToken();
    const refreshToken = this.authService.getApiRefreshToken();

    if (accessToken == null || refreshToken == null) {
      this.authService.logout();
      return throwError(() => 'Missing API token');
    }

    if (!this.apiTokenRefreshing) {
      this.apiTokenRefreshing = true;
      this.apiTokenSubject.next(null);
      return this.authService.refreshTokenRequest().pipe(
        catchError((error) => of({ refreshError: error })),
        switchMap((response: any) => {
          if ('refreshError' in response) {
            return this.retryApiRequestAfterAnotherTabRefresh(request, next, refreshToken, response.refreshError);
          }

          if (response.success) {
            this.authService.saveApiToken(response.data.token);
            this.authService.saveApiRefreshToken(response.data.refreshToken);
            this.apiTokenRefreshing = false;
            this.apiTokenSubject.next(response.data.token);
            const authReq = this.addTokenHeader(request, response.data.token);
            return next.handle(authReq).pipe(
              catchError((err: HttpErrorResponse) => {
                if (err.status == 401) {
                  this.authService.logout();
                }
                return throwError(err);
              })
            );
          } else {
            return this.retryApiRequestAfterAnotherTabRefresh(request, next, refreshToken, 'Cannot refresh token');
          }
        })
      );
    } else {
      return this.apiTokenSubject.pipe(
        filter((x) => x !== null),
        take(1),
        switchMap((token) => {
          const authReq = this.addTokenHeader(request, token);
          return next.handle(authReq).pipe(
            catchError((err: HttpErrorResponse) => {
              if (err.status == 401) {
                this.authService.logout();
              }

              return throwError(err);
            })
          );
        })
      );
    }
  }

  private handle401(request: HttpRequest<any>, next: HttpHandler): any {
    if (!this.oauthTokenRefreshing) {
      this.oauthTokenRefreshing = true;
      this.refreshTokenSubject.next(null);
      return this.authService.refreshOAuthTokenFromTheServer().pipe(
        switchMap((response) => {
          if (response.success) {
            this.authService.setOAuthToken(response.data!);
            this.oauthTokenRefreshing = false;
            this.refreshTokenSubject.next(response.data);
            const authReq = this.addTokenHeader(request, response.data!);
            return next.handle(authReq).pipe(
              catchError((err: HttpErrorResponse) => {
                if (err.status == 401) {
                  this.authService.logout();
                }
                return throwError(err);
              })
            );
          } else {
            this.oauthTokenRefreshing = false;
            this.authService.logout();
            return throwError('Cannot refresh token');
          }
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((x) => x !== null),
        take(1),
        switchMap((token) => {
          const authReq = this.addTokenHeader(request, token);
          return next.handle(authReq).pipe(
            catchError((err: HttpErrorResponse) => {
              if (err.status == 401) {
                this.authService.logout();
              }
              return throwError(err);
            })
          );
        })
      );
    }
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    let authReq: any = null;

    // U requestu k nam na API je nutne prilozit token, pokud server vrati 401, je nutne proste odhlasit
    if (this.isToAPI(req.url)) {
      const authReq = this.addTokenHeader(req, this.authService.getApiToken()!);
      return next.handle(authReq).pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.status == 401) {
            if (authReq.url.search('account/ews-login') === -1) {
              return this.handle401API(authReq, next);
            } else {
              return throwError(err);
            }
          }
          if (err.status == 403) {
            const translation = this.translateService.translate('common.contactToCheckAccount');
            window.alert(translation);
          }
          return throwError(err);
        })
      );
    }

    // U requestu ke Google / MS je nutne ziskat token ze serveru, pokud neni a handlit 401 pooci server refreshe
    if (this.isToMS(req.url) || this.isToGoogle(req.url)) {
      authReq = req;
      const token = this.authService.getOAuthToken();

      if (token != null) {
        // mame token, je potreba ho pouzit
        authReq = this.addTokenHeader(authReq, token);
        return next.handle(authReq).pipe(
          catchError((error) => {
            if (error instanceof HttpErrorResponse && error.status == 401) {
              return this.handle401(authReq, next);
            } else {
              return throwError(error);
            }
          })
        );
      } else {
        // token nemame, bude nutne ho ziskat

        // Pokud se jeste neziskava tak ho ziskame
        if (!this.oauthTokenObtaining) {
          this.oauthTokenObtaining = true;
          this.obtainTokenSubject.next(null);
          return this.authService.getOAuthTokenFromTheServer().pipe(
            switchMap((response) => {
              if (response.success) {
                this.authService.setOAuthToken(response.data!);
                this.oauthTokenObtaining = false;
                this.obtainTokenSubject.next(response.data);
                authReq = this.addTokenHeader(authReq, this.authService.getOAuthToken()!);
                return next.handle(authReq).pipe(
                  catchError((error) => {
                    if (error instanceof HttpErrorResponse && error.status == 401) {
                      return this.handle401(authReq, next);
                    } else {
                      return throwError(error);
                    }
                  })
                );
              } else {
                this.oauthTokenObtaining = false;
                this.authService.logout();
                return throwError('Could not get oAuth token from API');
              }
            })
          );
        } else {
          // Uz nekdo ziskava token, tak na to cekame
          return this.obtainTokenSubject.pipe(
            filter((token) => token !== null),
            take(1),
            switchMap((token) => {
              authReq = this.addTokenHeader(authReq, token);
              return next.handle(authReq).pipe(
                catchError((error) => {
                  if (error instanceof HttpErrorResponse && error.status == 401) {
                    return this.handle401(authReq, next);
                  } else {
                    return throwError(error);
                  }
                })
              );
            })
          );
        }
      }
    }

    // Pokud to neni ani k nam, ani k OUATH resource serveru, tak s tim nedelam nic.
    return next.handle(req);
  }
}
